import { useEffect, useRef, useState } from "react";
import type { TouchEvent } from "react";
import { Link } from "react-router-dom";
import type { VideoItem } from "@/types/video";

interface HeroCarouselProps {
  videos: VideoItem[];
}

export default function HeroCarousel({ videos }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);

  useEffect(() => {
    if (videos.length <= 1) return;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % videos.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [videos.length]);

  if (videos.length === 0) {
    return (
      <div className="flex h-[320px] items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 text-sm text-foreground-500">
        暂无视频资源
      </div>
    );
  }

  const goPrev = () => setIndex((prev) => (prev - 1 + videos.length) % videos.length);
  const goNext = () => setIndex((prev) => (prev + 1) % videos.length);

  const handleTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    touchStartX.current = event.touches[0].clientX;
  };

  const handleTouchEnd = (event: TouchEvent<HTMLDivElement>) => {
    if (touchStartX.current === null) return;
    const delta = event.changedTouches[0].clientX - touchStartX.current;
    if (delta > 50) goPrev();
    if (delta < -50) goNext();
    touchStartX.current = null;
  };

  return (
    <div
      className="relative h-[320px] w-full overflow-hidden rounded-lg bg-foreground-950 sm:h-[400px] lg:h-[480px]"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {videos.map((video, i) => (
        <div
          key={video.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
        >
          <img
            src={video.heroImage}
            alt={video.title}
            title={`${video.title} 高清视频推荐`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/40 to-foreground-950/20"></div>
          <div className="absolute inset-x-0 bottom-0 w-full px-5 pb-8 md:px-10 md:pb-12">
            <div className={i === index ? "animate-hero-fade" : ""}>
              <span className="inline-flex items-center gap-1 rounded-full bg-accent-500 px-3 py-1 text-xs font-medium text-white">
                <i className="ri-fire-line"></i>
                焦点推荐
              </span>
              <h2 className="mt-3 font-heading text-2xl font-bold text-white md:text-4xl">{video.title}</h2>
              <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-background-200 md:text-sm">
                <span className="rounded-md bg-white/15 px-2 py-0.5">{video.quality}</span>
                <span>{video.region}</span>
                <span>·</span>
                <span>{video.year}</span>
                <span>·</span>
                <span>{video.duration}</span>
              </div>
              <p className="mt-3 hidden max-w-2xl text-sm leading-relaxed text-background-200 sm:block">
                {video.summary}
              </p>
              <Link
                to={`/detail/${video.id}`}
                className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                查看详情
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={goPrev}
        aria-label="上一个"
        className="absolute left-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/20 text-white transition-colors hover:bg-white/30 md:flex"
      >
        <i className="ri-arrow-left-s-line text-xl"></i>
      </button>
      <button
        type="button"
        onClick={goNext}
        aria-label="下一个"
        className="absolute right-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/20 text-white transition-colors hover:bg-white/30 md:flex"
      >
        <i className="ri-arrow-right-s-line text-xl"></i>
      </button>

      <div className="absolute bottom-4 right-5 flex items-center gap-2 md:right-10">
        {videos.map((video, i) => (
          <button
            key={`dot-${video.id}`}
            type="button"
            aria-label={`切换到第${i + 1}个`}
            onClick={() => setIndex(i)}
            className={`h-2 rounded-full transition-all ${
              i === index ? "w-6 bg-accent-500" : "w-2 bg-white/50 hover:bg-white/80"
            }`}
          ></button>
        ))}
      </div>
    </div>
  );
}